import { motion, AnimatePresence } from 'framer-motion'
import { X, Award, Star, Trophy, Gift, Zap, Target, Sparkles, TrendingUp } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface PointsDetailsModalProps {
  isOpen: boolean
  onClose: () => void
}

const PointsDetailsModal = ({ isOpen, onClose }: PointsDetailsModalProps) => {
  const { t } = useLanguage()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-white/10 my-8"
            >
              <div className="sticky top-0 z-10 bg-gradient-to-r from-yellow-600/20 to-amber-600/20 border-b border-white/10 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                      <Trophy className="h-8 w-8 text-yellow-400" />
                      {t('points.modal.title')}
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">{t('points.modal.subtitle')}</p>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <X className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto px-6 py-8 max-h-[calc(90vh-100px)]">
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 rounded-2xl p-6 border border-yellow-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-yellow-500/20 rounded-xl">
                        <Sparkles className="h-6 w-6 text-yellow-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{t('points.modal.whatIs')}</h3>
                        <p className="text-gray-300 leading-relaxed">{t('points.modal.intro')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('points.modal.howToEarn')}</h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white/5 rounded-2xl p-6 border border-yellow-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                          <Target className="h-6 w-6 text-yellow-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-white">{t('points.modal.earn1.title')}</h4>
                          <p className="text-yellow-400 text-sm font-semibold">{t('points.modal.earn1.points')}</p>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm">{t('points.modal.earn1.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                      className="bg-white/5 rounded-2xl p-6 border border-amber-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                          <TrendingUp className="h-6 w-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-white">{t('points.modal.earn2.title')}</h4>
                          <p className="text-amber-400 text-sm font-semibold">{t('points.modal.earn2.points')}</p>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm">{t('points.modal.earn2.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="bg-white/5 rounded-2xl p-6 border border-green-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                          <Star className="h-6 w-6 text-green-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-white">{t('points.modal.earn3.title')}</h4>
                          <p className="text-green-400 text-sm font-semibold">{t('points.modal.earn3.points')}</p>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm">{t('points.modal.earn3.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="bg-white/5 rounded-2xl p-6 border border-blue-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                          <Zap className="h-6 w-6 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-white">{t('points.modal.earn4.title')}</h4>
                          <p className="text-blue-400 text-sm font-semibold">{t('points.modal.earn4.points')}</p>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm">{t('points.modal.earn4.description')}</p>
                    </motion.div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('points.modal.levels')}</h3>

                  <div className="space-y-4">
                    {[
                      { level: t('points.modal.level1.title'), points: '0-99', color: 'gray', icon: Star },
                      { level: t('points.modal.level2.title'), points: '100-499', color: 'blue', icon: Star },
                      { level: t('points.modal.level3.title'), points: '500-999', color: 'purple', icon: Award },
                      { level: t('points.modal.level4.title'), points: '1000-2499', color: 'yellow', icon: Trophy },
                      { level: t('points.modal.level5.title'), points: '2500+', color: 'amber', icon: Trophy },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className={`bg-gradient-to-r from-${item.color}-500/10 to-${item.color}-600/10 rounded-2xl p-5 border border-${item.color}-500/20`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 bg-${item.color}-500/20 rounded-xl flex items-center justify-center`}>
                              <item.icon className={`h-6 w-6 text-${item.color}-400`} />
                            </div>
                            <div>
                              <h4 className="text-white font-bold text-lg">{item.level}</h4>
                              <p className="text-gray-400 text-sm">{item.points} {t('points.modal.pointsLabel')}</p>
                            </div>
                          </div>
                          <div className={`px-4 py-2 bg-${item.color}-500/20 rounded-lg`}>
                            <p className={`text-${item.color}-400 font-bold`}>{t('points.modal.level' + (i + 1) + '.description')}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Gift className="h-6 w-6 text-pink-400" />
                    {t('points.modal.rewards')}
                  </h3>

                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      { icon: Gift, title: t('points.modal.reward1.title'), desc: t('points.modal.reward1.description'), color: 'pink' },
                      { icon: Trophy, title: t('points.modal.reward2.title'), desc: t('points.modal.reward2.description'), color: 'yellow' },
                      { icon: Star, title: t('points.modal.reward3.title'), desc: t('points.modal.reward3.description'), color: 'purple' },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className={`bg-gradient-to-br from-${item.color}-500/20 to-${item.color}-600/20 rounded-2xl p-6 border border-${item.color}-500/30`}
                      >
                        <div className={`w-12 h-12 bg-${item.color}-500/30 rounded-xl flex items-center justify-center mb-4`}>
                          <item.icon className={`h-6 w-6 text-${item.color}-400`} />
                        </div>
                        <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                        <p className="text-sm text-gray-300">{item.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-600/20 to-amber-600/20 rounded-2xl p-8 border border-yellow-500/30 text-center">
                  <h3 className="text-2xl font-bold text-white mb-3">{t('points.modal.cta')}</h3>
                  <p className="text-gray-300 mb-6">{t('points.modal.ctaDescription')}</p>
                  <button
                    onClick={onClose}
                    className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white font-semibold rounded-xl transition-all"
                  >
                    {t('points.modal.gotIt')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default PointsDetailsModal
